import MapIcon from '@mui/icons-material/Map';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import dompurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import AxiosConfig from '../../../../../../../config/AxiosConfig';
import languages from '../../../../../../helpers/utils/languages';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import ShippingAddressIcon from '../../../../../svg-icons/ShippingAddressIcon';
import LoadingButton from '../../../../../utils/common/LoadingButton';
import { getFormattedNote, hasValidChars } from '../utils';
import ShippingAddressHistory from './ShippingAddressHistory';
import { setSnackBar } from '../../../../../../states/app/staticSlice';
import { useDispatch } from 'react-redux';

const ShippingAddressForm = ({
  handleShowComponent,
  ticket,
  profile,
  shippingAddress,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    line1: '',
    line2: '',
    postalCode: '',
    city: '',
    country: '',
    phone: '',
    comment: '',
    formattedNote: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    line1: '',
    line2: '',
    postalCode: '',
    city: '',
    country: '',
    phone: '',
    line1Length: '',
    line2Length: '',
    commentLenght: '',
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [originalData, setOriginalData] = useState({});

  // Initialiser les données du formulaire avec l'adresse existante
  useEffect(() => {
    if (shippingAddress) {
      const initialData = {
        firstName: shippingAddress.firstName || '',
        lastName: shippingAddress.lastName || '',
        line1: shippingAddress.line1 || '',
        line2: shippingAddress.line2 || '',
        postalCode: shippingAddress.postalCode || '',
        city: shippingAddress.city || '',
        country: shippingAddress.country || '',
        phone: shippingAddress.phone || '',
      };
      setOriginalData(initialData);
      setFormData(initialData);
    }
  }, [ticket]);

  const handleInputChange = (field, value) => {
    const sanitizedValue = dompurify.sanitize(value);

    if (field === 'line1') {
      if (value.length > 35) {
        setErrors((prev) => ({
          ...prev,
          line1Length: "L'adresse ne doit pas dépasser 35 caractères",
        }));
        return;
      }
    }
    if (field === 'line2') {
      if (value.length > 35) {
        setErrors((prev) => ({
          ...prev,
          line2Length:
            "Le complément d'adresse ne doit pas dépasser 35 caractères",
        }));
        return;
      }
    }

    if (field === 'comment') {
      if (value.length > 200) {
        setErrors((prev) => ({
          ...prev,
          commentLenght: 'Le commentaire ne doit pas dépasser 200 caractères',
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: sanitizedValue,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.firstName) {
      newErrors.firstName = 'Le prénom est requis';
      isValid = false;
    } else if (formData.firstName.length < 3) {
      newErrors.firstName = 'Le prénom doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Le nom est requis';
      isValid = false;
    } else if (formData.lastName.length < 3) {
      newErrors.lastName = 'Le nom doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData.line1) {
      newErrors.line1 = "L'adresse est requise";
      isValid = false;
    } else if (formData.line1.length < 3) {
      newErrors.line1 = "L'adresse doit contenir au moins 3 caractères";
      isValid = false;
    }

    if (!formData.postalCode) {
      newErrors.postalCode = 'Le code postal est requis';
      isValid = false;
    } else if (formData.postalCode.length < 3) {
      newErrors.postalCode =
        'Le code postal doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData.city) {
      newErrors.city = 'La ville est requise';
      isValid = false;
    } else if (formData.city.length < 3) {
      newErrors.city = 'La ville doit contenir au moins 3 caractères';
      isValid = false;
    }

    if (!formData.phone) {
      newErrors.phone = 'Le numéro de téléphone est requis';
      isValid = false;
    } else if (formData.phone.length < 10) {
      newErrors.phone =
        'Le numéro de téléphone doit contenir au moins 10 caractères';
      isValid = false;
    }

    const formDataEntries = Object.entries(formData);
    for (const [key, value] of formDataEntries) {
      if (!hasValidChars(value)) {
        newErrors[key] = 'Le champ contient des caractères non valides';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Fonction pour obtenir les champs modifiés
  const getModifiedFields = () => {
    const modifiedFields = {};

    // Vérifie si prénom ou nom est modifié
    if (
      formData.firstName !== originalData.firstName ||
      formData.lastName !== originalData.lastName
    ) {
      modifiedFields.firstName = {
        oldValue: originalData.firstName,
        newValue: formData.firstName,
      };
      modifiedFields.lastName = {
        oldValue: originalData.lastName,
        newValue: formData.lastName,
      };
    }

    // Vérifie si code postal ou ville est modifié
    if (
      formData.postalCode !== originalData.postalCode ||
      formData.city !== originalData.city
    ) {
      modifiedFields.postalCode = {
        oldValue: originalData.postalCode,
        newValue: formData.postalCode,
      };
      modifiedFields.city = {
        oldValue: originalData.city,
        newValue: formData.city,
      };
    }

    // Ajoute les autres champs modifiés
    Object.entries(formData).forEach(([key, value]) => {
      if (
        key !== 'firstName' &&
        key !== 'lastName' &&
        key !== 'postalCode' &&
        key !== 'city' &&
        key !== 'country' &&
        value !== originalData[key]
      ) {
        modifiedFields[key] = {
          oldValue: originalData[key],
          newValue: value,
        };
      }
    });

    return modifiedFields;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }
    const formattedNote = getFormattedNote(
      profile?.firstName + ' ' + profile?.lastName,
      formData.comment,
      `
      Nom: ${formData.firstName} ${formData.lastName},
      Adresse: ${formData.line1} ${formData.postalCode} ${formData.city} ${formData.country},
      Téléphone: ${formData.phone}`,
      'shippingAddress'
    );
    setFormData((state) => ({ ...state, formattedNote }));
    setOpenConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setIsLoading(true);
    try {
      let response;
      await AxiosConfig.create(
        'put',
        `/orders/${ticket?.order?._id}/shippingAddress/`
      )
        .call({
          address: formData,
          ticketId: ticket?._id,
          note: formData.formattedNote,
          userId: profile?._id,
        })
        .exit((res) => (response = res?.data))
        .catch((error) => {
          if (error && error?.status > 300) {
            const messageError = `Error: ${error?.status} - ${error?.statusText}: ShippingAddressForm`;
            dispatch(
              setSnackBar({ open: true, message: messageError, color: 'error' })
            );
          } else {
            dispatch(setSnackBar({ open: false, message: null, color: null }));
          }
        });

      if (!response) {
        throw new Error("Erreur lors de la modification de l'adresse");
      }

      setOpenConfirmDialog(false);
      handleShowComponent(null);
    } catch (error) {
      console.error('Error updating address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fieldLabels = {
    firstName: 'Prénom',
    lastName: 'Nom',
    line1: 'Adresse',
    line2: "Complément d'adresse",
    postalCode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    phone: 'Téléphone',
  };

  const modifiedFields = getModifiedFields();
  const hasModifications = Object.keys(modifiedFields).length > 0;

  // Ajouter cette fonction pour construire l'URL Google Maps
  const getGoogleMapsUrl = () => {
    const address = `${formData.line1} ${formData.line2} ${formData.postalCode} ${formData.city} ${formData.country}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  };

  return (
    <>
      <Card
        elevation={5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ShippingAddressIcon fontSize="small" />
            <Typography color="primary">Addresse de livraison</Typography>
          </Box>
          <Button
            onClick={() => handleShowComponent(null)}
            variant="outlined"
            size="small"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <ArrowReturnIcon fontSize="small" />
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
          </Button>
        </Box>
      </Card>

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          // py: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <Typography sx={{ fontSize: '0.8em', mb: 1 }}>Prénom</Typography>
              <TextField
                size="small"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography sx={{ fontSize: '0.8em', mb: 1 }}>Nom</Typography>
              <TextField
                size="small"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
              />
            </FormControl>
          </Box>

          <FormControl fullWidth>
            <Typography sx={{ fontSize: '0.8em', mb: 1 }}>Téléphone</Typography>
            <TextField
              size="small"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
            />
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={{ fontSize: '0.8em', mb: 1 }}>Adresse</Typography>
            <TextField
              size="small"
              value={formData.line1}
              onChange={(e) => handleInputChange('line1', e.target.value)}
              error={Boolean(errors.line1)}
              helperText={
                errors.line1 || `${formData?.line1?.length || 0} / 35`
              }
            />
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={{ fontSize: '0.8em', mb: 1 }}>
              Complément d&apos;adresse
            </Typography>
            <TextField
              size="small"
              value={formData.line2}
              onChange={(e) => handleInputChange('line2', e.target.value)}
              error={Boolean(errors.line2)}
              helperText={
                errors.line2 || `${formData?.line2?.length || 0} / 35`
              }
            />
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ width: '30%' }}>
              <Typography sx={{ fontSize: '0.8em', mb: 1 }}>
                Code postal
              </Typography>
              <TextField
                size="small"
                value={formData.postalCode}
                onChange={(e) =>
                  handleInputChange('postalCode', e.target.value)
                }
                error={Boolean(errors.postalCode)}
                helperText={errors.postalCode}
              />
            </FormControl>

            <FormControl sx={{ width: '70%' }}>
              <Typography sx={{ fontSize: '0.8em', mb: 1 }}>Ville</Typography>
              <TextField
                size="small"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                error={Boolean(errors.city)}
                helperText={errors.city}
              />
            </FormControl>
          </Box>

          <FormControl fullWidth>
            <Typography sx={{ fontSize: '0.8em', mb: 1 }}>
              Pays (non modifiable)
            </Typography>
            {formData.country && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  height: '40px',
                  pl: 1,
                }}
              >
                <span
                  className={`fi fi-${formData.country.toLowerCase()}`}
                ></span>
                <span>
                  {
                    languages.find(
                      (country) => country.code2 === formData.country
                    )?.country
                  }
                </span>
              </Box>
            )}
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={{ fontSize: '0.8em', mb: 1 }}>
              Commentaire (optionnel)
            </Typography>
            <TextField
              multiline
              rows={4}
              value={formData.comment}
              onChange={(e) => handleInputChange('comment', e.target.value)}
              placeholder="Détaillez la raison de la modification..."
              size="small"
              error={Boolean(errors.comment)}
              helperText={
                errors.comment || `${formData?.comment?.length || 0} / 200`
              }
            />
          </FormControl>

          <LoadingButton
            isLoading={isLoading}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ width: '50%', alignSelf: 'center', mt: 2 }}
          >
            Modifier l&apos;adresse
          </LoadingButton>

          <ShippingAddressHistory orderId={ticket?.order?._id} />
        </Box>
      </Box>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{
          sx: { width: '600px', maxWidth: '600px' },
        }}
      >
        <DialogTitle>
          Confirmer la modification de l&apos;adresse de livraison
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!hasModifications ? (
            <Typography>Aucune modification n&apos;a été effectuée.</Typography>
          ) : (
            <>
              <Box sx={{ mt: 2 }}>
                {Object.entries(modifiedFields).map(
                  ([field, values], index, array) => {
                    // Skip les champs comment et formattedNote
                    if (field === 'comment' || field === 'formattedNote') {
                      return null;
                    }

                    // Gestion prénom/nom
                    if (field === 'firstName' || field === 'lastName') {
                      const otherField =
                        field === 'firstName' ? 'lastName' : 'firstName';
                      const otherValues = array.find(
                        ([f]) => f === otherField
                      )?.[1] || {
                        oldValue: originalData[otherField],
                        newValue: originalData[otherField],
                      };

                      if (field === 'firstName') {
                        return (
                          <Box key={field} sx={{ mb: 3 }}>
                            {/* Prénom et Nom */}
                            <Box sx={{ display: 'flex', gap: 4 }}>
                              {/* Prénom */}
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="primary"
                                  sx={{ fontWeight: 'bold', mb: 1 }}
                                >
                                  {fieldLabels[field]}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'error.main' }}
                                  >
                                    Ancien : {values.oldValue || '(vide)'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'success.main' }}
                                  >
                                    Nouveau : {values.newValue || '(vide)'}
                                  </Typography>
                                </Box>
                              </Box>
                              {/* Nom */}
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="primary"
                                  sx={{ fontWeight: 'bold', mb: 1 }}
                                >
                                  {fieldLabels['lastName']}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'error.main' }}
                                  >
                                    Ancien : {otherValues.oldValue || '(vide)'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'success.main' }}
                                  >
                                    Nouveau : {otherValues.newValue || '(vide)'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Téléphone (si modifié) */}
                            {modifiedFields.phone && (
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="primary"
                                  sx={{ fontWeight: 'bold', mb: 1 }}
                                >
                                  {fieldLabels['phone']}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'error.main' }}
                                  >
                                    Ancien :{' '}
                                    {modifiedFields.phone.oldValue || '(vide)'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'success.main' }}
                                  >
                                    Nouveau :{' '}
                                    {modifiedFields.phone.newValue || '(vide)'}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      }
                      return null;
                    }

                    // Gestion code postal/ville
                    if (field === 'postalCode' || field === 'city') {
                      const otherField =
                        field === 'postalCode' ? 'city' : 'postalCode';
                      const otherValues = array.find(
                        ([f]) => f === otherField
                      )?.[1] || {
                        oldValue: originalData[otherField],
                        newValue: originalData[otherField],
                      };

                      if (field === 'postalCode') {
                        return (
                          <Box key={field} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', gap: 4 }}>
                              {/* Code Postal */}
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="primary"
                                  sx={{ fontWeight: 'bold', mb: 1 }}
                                >
                                  {fieldLabels[field]}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'error.main' }}
                                  >
                                    Ancien : {values.oldValue || '(vide)'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'success.main' }}
                                  >
                                    Nouveau : {values.newValue || '(vide)'}
                                  </Typography>
                                </Box>
                              </Box>
                              {/* Ville */}
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="primary"
                                  sx={{ fontWeight: 'bold', mb: 1 }}
                                >
                                  {fieldLabels['city']}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'error.main' }}
                                  >
                                    Ancien : {otherValues.oldValue || '(vide)'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'success.main' }}
                                  >
                                    Nouveau : {otherValues.newValue || '(vide)'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        );
                      }
                      if (field === 'city') {
                        return null;
                      }
                    }

                    // Affichage des autres champs (sauf téléphone qui est déjà géré)
                    if (field !== 'phone') {
                      return (
                        <Box key={field} sx={{ mb: 3 }}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            sx={{ fontWeight: 'bold', mb: 1 }}
                          >
                            {fieldLabels[field]}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'error.main' }}
                            >
                              Ancien : {values.oldValue || '(vide)'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'success.main' }}
                            >
                              Nouveau : {values.newValue || '(vide)'}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  }
                )}
              </Box>
              <Typography
                sx={{
                  fontStyle: 'italic',
                  padding: 2,
                  border: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                {formData.formattedNote}
              </Typography>
              <Button
                startIcon={<MapIcon />}
                onClick={() => window.open(getGoogleMapsUrl(), '_blank')}
                sx={{ mb: 2, mt: 2, alignSelf: 'center' }}
                variant="outlined"
                color="primary"
                size="small"
              >
                Vérifier l&apos;adresse sur Google Maps
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Annuler</Button>
          <LoadingButton
            isLoading={isLoading}
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={!hasModifications}
          >
            Confirmer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShippingAddressForm;
